import { opt } from '../lib/media';
import usePageMeta from '../hooks/usePageMeta';
import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Leaf, Hand, Gem } from 'lucide-react';

const values = [
  { icon: Hand, title: 'Heritage Craft', text: 'Contemporary Indo-Western pieces inspired by the richness of Indian aesthetics.' },
  { icon: Gem, title: 'Distinctive Design', text: 'Modern silhouettes, distinctive patterns and unexpected colour stories.' },
  { icon: Sparkles, title: 'Made For You', text: 'Every creation is designed to make you feel beautiful, confident and uniquely yourself.' },
  { icon: Leaf, title: 'Individuality', text: 'Silhouettes that move beyond convention to complement every body and every woman.' },
];

function TextBlock({ eyebrow, title, children, dark }) {
  return (
    <section className={dark ? 'bg-burgundy-900' : 'bg-white'}>
      <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
        <p className={`font-display text-4xl md:text-5xl mb-2 ${dark ? 'text-cream' : 'text-burgundy-900'}`}>{title}</p>
        {eyebrow && <p className="text-gold-dark tracking-luxe text-xs uppercase mb-6">{eyebrow}</p>}
        <div className={`gold-divider !mx-0 !my-6`} />
        <div className={`space-y-5 text-base md:text-lg leading-relaxed ${dark ? 'text-cream/80' : 'text-burgundy-900/75'}`}>
          {children}
        </div>
      </div>
    </section>
  );
}

export default function About() {
  usePageMeta('About Hitera | Designer Wear Brand – Mumbai, India', 'The story of Hitera (Hitéra) — a luxury designer wear house from Mumbai, India, blending Indian heritage craft with contemporary couture.');
  return (
    <div className="bg-white">
      {/* Hero */}
      <div className="relative h-[70vh] overflow-hidden bg-burgundy-950">
        <img src={opt("https://customer-assets-jt897jd0.emergentagent.net/job_hitera-boutique/artifacts/lnuxd9ye_20260825_172613_IMG_4455.webp", 64)} alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-50" />
        <img src={opt("https://customer-assets-jt897jd0.emergentagent.net/job_hitera-boutique/artifacts/lnuxd9ye_20260825_172613_IMG_4455.webp", 1200)} alt="Our Story" fetchPriority="high" decoding="async" className="relative w-full h-full object-contain" />
        <div className="absolute inset-0 bg-gradient-to-b from-burgundy-950/70 via-burgundy-950/15 to-transparent" />
        <div className="absolute inset-0 flex flex-col items-center justify-start text-center px-6 pt-8 md:pt-10">
          <p className="text-gold tracking-luxe text-xs uppercase mb-3 fade-up">The House of HITÉRA</p>
          <h1 className="font-display text-4xl md:text-6xl text-cream fade-up">Our Story</h1>
        </div>
      </div>

      <TextBlock title="About Us">
        <p className="tracking-wide uppercase text-sm text-gold-dark">Where Indian heritage meets modern expression.</p>
        <p>At HITÉRA, we believe fashion is more than what you wear — it is a reflection of who you are.</p>
        <p>We create contemporary Indo-Western pieces inspired by the richness of Indian aesthetics, brought to life through modern silhouettes, distinctive patterns and unexpected colour stories.</p>
        <p>Every creation is designed to make you feel beautiful, confident and uniquely yourself.</p>
      </TextBlock>

      <TextBlock title="My Story" dark>
        <p className="tracking-wide uppercase text-sm text-gold">It started with a love for creating.</p>
        <p>Behind HITÉRA is a simple passion — the joy of designing clothes.</p>
        <p>I have always been drawn to the way a colour, pattern, silhouette or tiny detail can transform not just an outfit, but the way a woman feels in it.</p>
        <p>My inspiration comes from blending the beauty of Indian design with a contemporary perspective — creating Indo-Western pieces that feel fresh, distinctive and effortless.</p>
        <p>But what inspires me most is my clients. I love creating something that feels personal to them — something that makes them look in the mirror and think: <em className="text-gold">“This feels like me.”</em></p>
        <p>That feeling is the heart of HITÉRA.</p>
      </TextBlock>

      {/* Our Brand — split with image */}
      <section className="grid md:grid-cols-2">
        <div className="bg-cream flex items-center justify-center px-8 py-16 order-2 md:order-1">
          <div className="max-w-md">
            <p className="font-display text-4xl text-burgundy-900 mb-2">Our Brand</p>
            <p className="text-gold-dark tracking-luxe text-xs uppercase mb-6">Modern Indian Luxury</p>
            <div className="space-y-4 text-burgundy-900/75 leading-relaxed text-sm md:text-base">
              <p>Designed for the woman who embraces her individuality. HITÉRA is a celebration of modern Indian luxury.</p>
              <p>We create silhouettes that move beyond conventional fashion — designed to complement different body types while allowing every woman to experience fashion in her own way.</p>
              <p>From unique colour combinations to statement details and unexpected patterns, every design carries its own identity.</p>
            </div>
          </div>
        </div>
        <div className="h-[55vh] md:h-auto img-zoom overflow-hidden order-1 md:order-2">
          <img src={opt("https://customer-assets-jt897jd0.emergentagent.net/job_hitera-boutique/artifacts/ue1okql7_IMG-20260930-WA0008.jpg", 1000)} alt="Our Brand" loading="lazy" decoding="async" width="900" height="1600" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* Our Project 2026 — coming soon */}
      <section className="bg-burgundy-950 text-center">
        <div className="max-w-3xl mx-auto px-6 py-20 md:py-28">
          <p className="text-gold tracking-luxe text-xs uppercase mb-4">Coming Soon — 2026</p>
          <h2 className="font-display text-4xl md:text-5xl text-cream mb-3">Our Project 2026</h2>
          <div className="gold-divider" />
          <div className="space-y-5 text-cream/80 leading-relaxed text-base md:text-lg">
            <p>A new chapter in modern Indian luxury. HITÉRA 2026 is an exploration of Indian heritage through a contemporary imagination.</p>
            <p>Expect distinctive silhouettes, refined Indo-Western detailing, expressive colour combinations, statement patterns, and pieces designed to make an entrance while remaining effortlessly wearable.</p>
            <p>This collection is created for the woman who doesn’t simply follow fashion — <span className="text-gold">she defines her own.</span></p>
            <p className="font-display text-2xl text-cream pt-2">Something beautiful is about to unfold.</p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-[1300px] mx-auto px-6 py-20 md:py-24">
        <div className="text-center mb-14">
          <h2 className="font-display text-4xl md:text-5xl text-burgundy-900">The HITÉRA Promise</h2>
          <div className="gold-divider" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((v) => (
            <div key={v.title} className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full border border-gold flex items-center justify-center mb-5">
                <v.icon size={26} className="text-gold-dark" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-xl text-burgundy-900 mb-2">{v.title}</h3>
              <p className="text-burgundy-900/60 text-sm leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Thank You */}
      <section className="bg-burgundy-900 text-center">
        <div className="max-w-2xl mx-auto px-6 py-20 md:py-24">
          <h2 className="font-display text-4xl md:text-5xl text-cream mb-3">Thank You</h2>
          <div className="gold-divider" />
          <div className="space-y-4 text-cream/80 leading-relaxed">
            <p>Thank you for being a part of the HITÉRA story. What started with a love for design is becoming a journey shaped by every woman who chooses to wear HITÉRA.</p>
            <p>Thank you for trusting us with your celebrations, your moments, and your stories. This is only the beginning.</p>
            <p className="text-gold">With love, HITÉRA</p>
            <p className="font-display text-2xl text-cream pt-2">Step into your own kind of luxury.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/shop" className="bg-cream text-burgundy-950 px-8 py-3.5 text-xs tracking-wideluxe uppercase hover:bg-gold transition-colors">Shop Now</Link>
            <Link to="/happy-clients" className="border border-gold text-gold px-8 py-3.5 text-xs tracking-wideluxe uppercase hover:bg-gold hover:text-burgundy-950 transition-colors">Happy Clients</Link>
          </div>
          <p className="text-gold/70 text-xs tracking-wideluxe uppercase mt-6">Follow The Journey · @hitera_</p>
        </div>
      </section>
    </div>
  );
}
